/* Emsal ürün sayfası (sections/main-product-emsal.liquid) davranışları. Bölüm başına bir kez kurulur (init).
   Bölümler (aşağıda sırayla):
   1. Yardımcılar: güvenli adres, YouTube/Vimeo gömme adresi, metin etiketleri (taggedText → kz.js kzTags)
   2. Ürün içeriği: assets/emsal-products.json'dan ürün ID'sine göre modül videoları, inceleme videosu, teknik özellikler
      (JSON'u düzenlemek için tools/emsal/; ayrıntı tools/emsal/README.md)
   3. init: indirim kampanyası kutusu, SSS, galeri + tam ekran görüntüleyici, sabit satın alma çubuğu, Taksit ve
      Ön Sipariş pencereleri
   Kurallar: sayfa açılışında video/iframe indirilmez (ekrana yaklaşınca), kaydırmada düzen okuması yok, animasyonlar
   CSS'te (transform/opacity). Tema editöründe bölüm yeniden yüklenince eski dinleyiciler kaldırılır (offs). */
(() => {
  const configRequests = new Map();
  // Bölüm başına söküm listesi: tema editöründe bölüm yeniden çizilince belge/pencere dinleyicileri ve abonelikler kalkar
  const teardown = new Map();
  const configuredUrl = (value) => typeof value === 'string' && value.trim() !== '' && !value.trim().startsWith('PLACEHOLDER_');
  const safeUrl = (value) => {
    if (!configuredUrl(value)) return '';
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (_) {
      return '';
    }
  };
  const externalVideoUrl = (value) => {
    const source = safeUrl(value);
    if (!source) return '';
    const url = new URL(source);
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') return `https://www.youtube.com/embed/${url.pathname.split('/').filter(Boolean)[0] || ''}`;
    if (host.endsWith('youtube.com')) {
      const id = url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop();
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }
    if (host === 'vimeo.com') {
      const id = url.pathname.split('/').filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}` : '';
    }
    return source;
  };

  // Metin etiketleri: temanın tek çeviricisi (assets/kz.js window.kzTags, snippets/kz-tags.liquid ile aynı kurallar).
  // kz.js yoksa yazı etiketsiz, güvenli düz yazı olarak çıkar
  const escapeText = (value) => String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const taggedText = (value, fallback = '') => {
    const source = String(value || fallback || '');
    return window.kzTags ? window.kzTags.text(source) : escapeText(source);
  };

  const setTaggedText = (element, value, fallback = '') => {
    if (element) element.innerHTML = taggedText(value, fallback);
  };

  // Sessiz döngü videosu yalnız ekrana yakınken oynar (veri ve pil); IntersectionObserver yoksa hemen
  const videoIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => entries.forEach((e) => {
    const v = e.target;
    if (e.isIntersecting) {
      if (!v.getAttribute('src')) { v.src = v.dataset.src; v.preload = 'auto'; }
      v.play()?.catch?.(() => {});
    } else v.pause();
  }), { rootMargin: '50% 0px' }) : null;
  // Veri tasarrufu / 2G: kendiliğinden inmez, oynat düğmesiyle (CLAUDE.md video kuralı)
  const conn = navigator.connection || {};
  const slowNet = !!conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '');
  const playLazy = (video) => {
    if (slowNet) { video.src = video.dataset.src; video.controls = true; video.preload = 'none'; return; }
    if (videoIO) videoIO.observe(video);
    else { video.src = video.dataset.src; video.autoplay = true; video.preload = 'metadata'; }
  };

  const renderProductContent = async (root) => {
    const feature = root.querySelector('[data-kla-feature]');
    const reviewVideoSection = root.querySelector('[data-kla-review-video]');
    const specsSection = root.querySelector('[data-kla-specs]');
    const hideDynamicSections = () => {
      if (feature) feature.hidden = true;
      if (reviewVideoSection) reviewVideoSection.hidden = true;
      if (specsSection) specsSection.hidden = true;
    };
    hideDynamicSections();

    // Editör yedeği (bölümdeki "Modül videosu" blokları ve "İnceleme videosu" ayarları): JSON'da bu ürün için yoksa
    let fallback = {};
    try { fallback = JSON.parse(root.querySelector('[data-kla-fallback]')?.textContent || '{}') || {}; } catch (_) { fallback = {}; }
    const validModules = (list) => (Array.isArray(list) ? list : []).filter((item) => {
      const type = String(item?.media?.type || '').toLowerCase();
      return configuredUrl(item?.media?.url) && ['video', 'mp4', 'webm', 'image', 'gif', 'external_video', 'youtube', 'vimeo'].includes(type);
    });

    const configUrl = root.dataset.productConfigUrl;
    let payload = null;
    if (configUrl && root.dataset.productId) {
      try {
        if (!configRequests.has(configUrl)) {
          configRequests.set(configUrl, fetch(configUrl, { credentials: 'same-origin' }).then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          }));
        }
        payload = await configRequests.get(configUrl);
      } catch (error) {
        console.warn('[Emsal product data]', error);
      }
    }
    try {
      const json = payload?.products?.[String(root.dataset.productId)] || {};
      const jsonModules = validModules(json.advanced_modules);
      const jsonReview = configuredUrl(json.review_video_url);
      const productData = {
        ...json,
        advanced_modules: jsonModules.length ? jsonModules : validModules(fallback.advanced_modules),
        review_video_url: jsonReview ? json.review_video_url : fallback.review_video_url,
        review_video_file: jsonReview ? '' : fallback.review_video_file,
      };

      if (feature) {
        const titles = feature.querySelector('[data-kla-feature-titles]');
        const track = feature.querySelector('[data-kla-feature-track]');
        const dots = feature.querySelector('[data-kla-feature-dots]');
        const previousButton = feature.querySelector('[data-kla-feature-prev]');
        const nextButton = feature.querySelector('[data-kla-feature-next]');
        titles?.replaceChildren();
        track?.replaceChildren();
        dots?.replaceChildren();

        const modules = productData.advanced_modules;
        modules.forEach((item, index) => {
          const media = item.media || {};
          const type = String(media.type || '').toLowerCase();
          const mediaUrl = ['external_video', 'youtube', 'vimeo'].includes(type) ? externalVideoUrl(media.url) : safeUrl(media.url);
          if (!mediaUrl) return;

          const title = document.createElement('h2');
          title.className = `kla-feature__title${index === 0 ? ' is-active' : ''}`;
          title.dataset.klaFeatureTitle = String(index);
          setTaggedText(title, item.module_title, 'Advanced Modülü');
          titles?.append(title);

          const slide = document.createElement('li');
          slide.className = 'kla-feature__slide';
          slide.dataset.klaFeatureSlide = String(index);
          const mediaBox = document.createElement('div');
          const ratioClass = media.aspect_ratio === '9:16' ? 'portrait' : 'landscape';
          const fitClass = media.fit === 'contain' ? 'contain' : 'cover';
          mediaBox.className = `kla-feature__media kla-feature__media--${ratioClass} kla-feature__media--${fitClass}`;

          if (['image', 'gif'].includes(type)) {
            const image = document.createElement('img');
            image.src = mediaUrl;
            image.alt = item.title || item.module_title || '';
            image.loading = 'lazy';
            mediaBox.append(image);
          } else if (['external_video', 'youtube', 'vimeo'].includes(type)) {
            const frame = document.createElement('iframe');
            frame.src = mediaUrl;
            frame.title = item.title || item.module_title || '';
            frame.loading = 'lazy';
            frame.allow = 'autoplay; encrypted-media; picture-in-picture';
            frame.allowFullscreen = true;
            mediaBox.append(frame);
          } else {
            // Video açılışta inmez: ekrana yarım ekran kala oynar, uzaklaşınca durur (playLazy)
            const video = document.createElement('video');
            video.dataset.src = mediaUrl;
            video.loop = true;
            video.muted = true;
            video.playsInline = true;
            video.preload = 'none';
            mediaBox.append(video);
            playLazy(video);
          }

          const counter = document.createElement('span');
          counter.className = 'kla-feature__counter';
          counter.textContent = `${index + 1} / ${modules.length}`;
          mediaBox.append(counter);
          slide.append(mediaBox);

          if (item.title) {
            const name = document.createElement('h3');
            name.className = 'kla-feature__name';
            setTaggedText(name, item.title);
            slide.append(name);
          }
          if (item.description) {
            const copy = document.createElement('div');
            copy.className = 'kla-feature__copy';
            const paragraph = document.createElement('p');
            setTaggedText(paragraph, item.description);
            copy.append(paragraph);
            slide.append(copy);
          }
          track?.append(slide);

          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = `kla-dot${index === 0 ? ' is-active' : ''}`;
          dot.dataset.klaFeatureDot = String(index);
          dot.setAttribute('aria-label', String(index + 1));
          dots?.append(dot);
        });
        const renderedCount = track?.querySelectorAll('[data-kla-feature-slide]').length || 0;
        if (previousButton) previousButton.hidden = renderedCount < 2;
        if (nextButton) nextButton.hidden = renderedCount < 2;
        if (dots) dots.hidden = renderedCount < 2;
        feature.hidden = renderedCount === 0;
      }

      if (reviewVideoSection) {
        const mediaContainer = reviewVideoSection.querySelector('[data-kla-review-video-media]');
        mediaContainer?.replaceChildren();
        const reviewVideoUrl = externalVideoUrl(productData.review_video_url);
        if (mediaContainer && reviewVideoUrl) {
          const frame = document.createElement('iframe');
          frame.src = reviewVideoUrl;
          frame.title = 'Ürün inceleme videosu';
          frame.loading = 'lazy';
          frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
          frame.allowFullscreen = true;
          mediaContainer.append(frame);
          reviewVideoSection.hidden = false;
        } else if (mediaContainer && safeUrl(productData.review_video_file)) {
          // Shopify'a yüklenen video (bölüm ayarı): oynat'a basılınca iner
          const video = document.createElement('video');
          video.src = safeUrl(productData.review_video_file);
          video.controls = true;
          video.playsInline = true;
          video.preload = 'none';
          mediaContainer.append(video);
          reviewVideoSection.hidden = false;
        }
      }

      if (specsSection) {
        const list = specsSection.querySelector('[data-kla-specs-list]');
        const heading = specsSection.querySelector('[data-kla-specs-title]');
        list?.replaceChildren();
        const specs = (Array.isArray(productData.technical_specs) ? productData.technical_specs : []).filter((item) => item && (item.label || item.value));
        specs.forEach((item) => {
          const row = document.createElement('li');
          const label = document.createElement('strong');
          const value = document.createElement('span');
          setTaggedText(label, item.label);
          setTaggedText(value, item.value);
          row.append(label, value);
          list?.append(row);
        });
        if (heading && productData.technical_specs_heading) setTaggedText(heading, productData.technical_specs_heading);
        const details = specsSection.querySelector('details');
        if (details) details.open = true;
        specsSection.hidden = specs.length === 0;
      }
      if (window.kzOkFit) window.kzOkFit(root);
    } catch (error) {
      hideDynamicSections();
      console.warn('[Emsal product data]', error);
    }
  };

  const initFeature = (root) => {
    const feature = root.querySelector('[data-kla-feature]');
    if (!feature || feature.hidden) return;
    const track = feature.querySelector('[data-kla-feature-track]');
    const slides = [...feature.querySelectorAll('[data-kla-feature-slide]')];
    const titles = [...feature.querySelectorAll('[data-kla-feature-title]')];
    const dots = [...feature.querySelectorAll('[data-kla-feature-dot]')];
    const previousButton = feature.querySelector('[data-kla-feature-prev]');
    const nextButton = feature.querySelector('[data-kla-feature-next]');
    let active = 0;
    const setActive = (index, scroll = true) => {
      active = Math.max(0, Math.min(index, slides.length - 1));
      titles.forEach((el, i) => el.classList.toggle('is-active', i === active));
      dots.forEach((el, i) => el.classList.toggle('is-active', i === active));
      previousButton?.classList.toggle('is-hidden', active === 0);
      nextButton?.classList.toggle('is-hidden', active === slides.length - 1);
      if (scroll) slides[active]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    };
    dots.forEach((el, i) => el.addEventListener('click', () => setActive(i)));
    previousButton?.addEventListener('click', () => setActive(active - 1));
    nextButton?.addEventListener('click', () => setActive(active + 1));
    setActive(0, false);
    let frame;
    track?.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setActive(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)), false));
    }, { passive: true });
  };

  const init = (root) => {
    if (!root || root.dataset.klaReady === 'true') return;
    root.dataset.klaReady = 'true';
    const key = root.dataset.section || root.id;
    teardown.get(key)?.forEach((off) => off());
    const offs = [];
    teardown.set(key, offs);
    const ctl = new AbortController();
    offs.push(() => ctl.abort());
    const on = { signal: ctl.signal };
    // Dawn varyant aboneliği (pubsub.js); yoksa hiçbir şey yapmaz
    const onVariant = (fn) => {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;
      const off = subscribe(PUB_SUB_EVENTS.variantChange, fn);
      if (typeof off === 'function') offs.push(off);
    };

    const money = (cents) => `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(Math.ceil(Number(cents || 0) / 100))}TL`;

    const campaign = root.querySelector('[data-kla-discount-campaign]');
    if (campaign) {
      const priceBox = campaign.querySelector('.kla-info__price');
      const animationEnabled = campaign.dataset.animationEnabled === 'true';
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const sourceHtml = (selector) => campaign.querySelector(selector)?.innerHTML || '';

      const addPriceLabel = (target, html, priceType) => {
        if (!target) return;
        target.classList.add('kla-campaign__price-group', `kla-campaign__price-group--${priceType}`);
        if (!html || target.querySelector('.kla-campaign__price-label')) return;
        const label = document.createElement('span');
        label.className = 'kla-campaign__price-label';
        label.innerHTML = html;
        target.prepend(label);
      };
      const decoratePrices = () => {
        if (!campaign.classList.contains('is-live') || !priceBox) return;
        const saleItem = priceBox.querySelector('.price__sale .price-item--sale');
        const regularItem = priceBox.querySelector('.price__sale .price-item--regular');
        if (saleItem && !saleItem.querySelector('.kla-campaign__sale-value')) {
          const value = document.createElement('span');
          value.className = 'kla-campaign__sale-value';
          Array.from(saleItem.childNodes).filter((node) => !node.classList?.contains('kla-campaign__price-label')).forEach((node) => value.appendChild(node));
          saleItem.appendChild(value);
        }
        addPriceLabel(saleItem, sourceHtml('[data-kla-campaign-sale-source]'), 'sale');
        addPriceLabel(regularItem?.parentElement, sourceHtml('[data-kla-campaign-regular-source]'), 'regular');
      };
      const updateCampaign = () => {
        if (!root.isConnected) return;
        const currentPrice = Number(campaign.dataset.currentPrice || 0);
        const comparePrice = Number(campaign.dataset.comparePrice || 0);
        const live = currentPrice > 0 && comparePrice > currentPrice;
        campaign.classList.toggle('is-live', live);
        campaign.classList.toggle('is-animated', live && animationEnabled && !reducedMotion);
        campaign.classList.add('is-ready');
        if (live) requestAnimationFrame(decoratePrices);
      };
      updateCampaign();
      onVariant(({ data }) => {
        if (data.sectionId !== root.dataset.section || !data.variant) return;
        campaign.dataset.currentPrice = String(data.variant.price || 0);
        campaign.dataset.comparePrice = String(data.variant.compare_at_price || 0);
        updateCampaign();
      });
      // Dawn varyant değişince fiyat HTML'ini yeniden yazar: etiketler yazım bitince bir kez yeniden eklenir
      // (önce 0/80/240 ms'lik üç zamanlayıcıyla tahmin ediliyordu)
      if (priceBox && 'MutationObserver' in window) {
        let queued = 0;
        const mo = new MutationObserver(() => { if (!queued) queued = requestAnimationFrame(() => { queued = 0; decoratePrices(); }); });
        mo.observe(priceBox, { childList: true, subtree: true });
        offs.push(() => mo.disconnect());
      }
      // Ekrandan çıkınca kutunun animasyonları durur (CSS .is-off); görünüm aynı
      if ('IntersectionObserver' in window) {
        const write = window.kzFps?.write || ((fn) => fn()); // karenin sonunda (kz.js kare kuralı)
        const io = new IntersectionObserver((es) => { const off = !es[es.length - 1].isIntersecting; /* en yeni kayıt */ write(() => campaign.classList.toggle('is-off', off)); });
        io.observe(campaign);
        offs.push(() => io.disconnect());
      }
    }

    root.querySelectorAll('[data-kla-faq-toggle]').forEach((button) => {
      const item = button.closest('[data-kla-faq-item]');
      const answer = item?.querySelector('[data-kla-faq-answer]');
      if (!item || !answer) return;
      const setOpen = (open) => {
        item.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
        answer.setAttribute('aria-hidden', String(!open));
      };
      setOpen(item.classList.contains('is-open'));
      button.addEventListener('click', () => setOpen(!item.classList.contains('is-open')));
    });

    const gallery = root.querySelector('[data-kla-gallery]');
    if (gallery) {
      const slides = [...gallery.querySelectorAll('[data-kla-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-kla-thumb]')];
      const dots = [...gallery.querySelectorAll('[data-kla-dot]')];
      const track = gallery.querySelector('[data-kla-track]');
      const previousButton = gallery.querySelector('[data-kla-gallery-prev]');
      const nextButton = gallery.querySelector('[data-kla-gallery-next]');
      let active = 0;
      const setActive = (index, scroll = true) => {
        if (!slides.length) return;
        active = Math.max(0, Math.min(index, slides.length - 1));
        slides.forEach((el, i) => el.classList.toggle('is-active', i === active));
        thumbs.forEach((el, i) => el.classList.toggle('is-active', i === active));
        dots.forEach((el, i) => el.classList.toggle('is-active', i === active));
        previousButton?.classList.toggle('is-hidden', active === 0);
        nextButton?.classList.toggle('is-hidden', active === slides.length - 1);
        // Küçük resim şeridi kendi içinde kayar (sayfa kaymaz; önce scrollIntoView varyant değişince sayfayı yukarı çekiyordu)
        const thumb = thumbs[active], strip = thumb?.parentElement;
        if (thumb && strip && strip.scrollHeight > strip.clientHeight) {
          const r = thumb.getBoundingClientRect(), c = strip.getBoundingClientRect();
          if (r.top < c.top) strip.scrollTop -= c.top - r.top;
          else if (r.bottom > c.bottom) strip.scrollTop += r.bottom - c.bottom;
        }
        if (scroll && matchMedia('(max-width: 989px)').matches) slides[active]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      };
      thumbs.forEach((el, i) => el.addEventListener('click', () => setActive(i)));
      dots.forEach((el, i) => el.addEventListener('click', () => setActive(i)));
      previousButton?.addEventListener('click', () => setActive(active - 1));
      nextButton?.addEventListener('click', () => setActive(active + 1));
      setActive(0, false);
      let frame;
      track?.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const next = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          if (next !== active) setActive(next, false);
        });
      }, { passive: true });

      const lightbox = root.querySelector('[data-kla-lightbox]');
      const lightboxImage = lightbox?.querySelector('[data-kla-lightbox-image]');
      const lightboxBlurImage = lightbox?.querySelector('[data-kla-lightbox-blur-image]');
      const zoomSlides = slides.filter((slide) => slide.querySelector('[data-kla-zoom]'));
      let zoomIndex = 0;
      let zoomScale = 1;
      let zoomPanX = 0;
      let zoomPanY = 0;
      let zoomTransitionId = 0;
      const zoomPrevious = lightbox?.querySelector('[data-kla-lightbox-prev]');
      const zoomNext = lightbox?.querySelector('[data-kla-lightbox-next]');
      const zoomThumbs = [...(lightbox?.querySelectorAll('[data-kla-lightbox-thumb]') || [])];
      const zoomStage = lightbox?.querySelector('[data-kla-lightbox-stage]');
      const clampZoomPan = () => {
        if (!lightboxImage || zoomScale <= 1) {
          zoomPanX = 0;
          zoomPanY = 0;
          return;
        }
        const maxX = lightboxImage.clientWidth * (zoomScale - 1) / 2;
        const maxY = lightboxImage.clientHeight * (zoomScale - 1) / 2;
        zoomPanX = Math.max(-maxX, Math.min(maxX, zoomPanX));
        zoomPanY = Math.max(-maxY, Math.min(maxY, zoomPanY));
      };
      const applyZoom = () => {
        if (!lightboxImage) return;
        clampZoomPan();
        lightboxImage.style.transform = `translate3d(${zoomPanX}px,${zoomPanY}px,0) scale(${zoomScale})`;
      };
      const resetZoom = () => {
        zoomScale = 1;
        zoomPanX = 0;
        zoomPanY = 0;
        applyZoom();
      };
      const showZoom = (index) => {
        if (!lightbox || !lightboxImage || !zoomSlides.length) return;
        const opening = lightbox.hidden;
        const transitionId = ++zoomTransitionId;
        zoomIndex = Math.max(0, Math.min(index, zoomSlides.length - 1));
        resetZoom();
        const button = zoomSlides[zoomIndex].querySelector('[data-kla-zoom]');
        const source = button.dataset.klaZoom;
        const alt = button.querySelector('img')?.alt || '';
        lightboxImage.classList.add('is-changing');
        lightboxBlurImage?.classList.add('is-changing');
        window.setTimeout(() => {
          if (transitionId !== zoomTransitionId) return;
          const reveal = () => {
            if (transitionId !== zoomTransitionId) return;
            requestAnimationFrame(() => {
              lightboxImage.classList.remove('is-changing');
              lightboxBlurImage?.classList.remove('is-changing');
            });
          };
          lightboxImage.addEventListener('load', reveal, { once: true });
          lightboxImage.src = source;
          lightboxImage.alt = alt;
          applyZoom();
          if (lightboxBlurImage) lightboxBlurImage.src = source;
          if (lightboxImage.complete) reveal();
        }, opening ? 0 : 140);
        lightbox.querySelector('[data-kla-lightbox-counter]').textContent = `${zoomIndex + 1} / ${zoomSlides.length}`;
        zoomThumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === zoomIndex));
        zoomThumbs[zoomIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        zoomPrevious?.classList.toggle('is-hidden', zoomIndex === 0);
        zoomNext?.classList.toggle('is-hidden', zoomIndex === zoomSlides.length - 1);
        lightbox.hidden = false;
        document.body.style.overflow = 'hidden';
      };
      slides.forEach((slide) => slide.querySelector('[data-kla-zoom]')?.addEventListener('click', () => showZoom(zoomSlides.indexOf(slide))));
      const closeZoom = () => { ++zoomTransitionId; resetZoom(); if (lightbox) lightbox.hidden = true; document.body.style.overflow = ''; };
      lightbox?.querySelector('[data-kla-lightbox-close]')?.addEventListener('click', closeZoom);
      zoomPrevious?.addEventListener('click', () => showZoom(zoomIndex - 1));
      zoomNext?.addEventListener('click', () => showZoom(zoomIndex + 1));
      zoomThumbs.forEach((thumb, index) => thumb.addEventListener('click', () => showZoom(index)));
      let touchStartX = 0;
      let touchStartY = 0;
      let panStartX = 0;
      let panStartY = 0;
      let pinchStartDistance = 0;
      let pinchStartScale = 1;
      let gestureMode = '';
      let pinchUsed = false;
      let lastTapAt = 0;
      const touchDistance = (touches) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
      zoomStage?.addEventListener('touchstart', (event) => {
        lightboxImage?.classList.add('is-gesturing');
        if (event.touches.length >= 2) {
          gestureMode = 'pinch';
          pinchUsed = true;
          pinchStartDistance = touchDistance(event.touches);
          pinchStartScale = zoomScale;
          return;
        }
        const touch = event.touches[0];
        touchStartX = touch?.clientX || 0;
        touchStartY = touch?.clientY || 0;
        panStartX = zoomPanX;
        panStartY = zoomPanY;
        pinchUsed = false;
        gestureMode = zoomScale > 1 ? 'pan' : 'swipe';
      }, { passive: true });
      zoomStage?.addEventListener('touchmove', (event) => {
        if (event.touches.length >= 2) {
          event.preventDefault();
          if (gestureMode !== 'pinch') {
            gestureMode = 'pinch';
            pinchUsed = true;
            pinchStartDistance = touchDistance(event.touches);
            pinchStartScale = zoomScale;
          }
          zoomScale = Math.max(1, Math.min(4, pinchStartScale * touchDistance(event.touches) / Math.max(pinchStartDistance, 1)));
          applyZoom();
          return;
        }
        if (event.touches.length === 1 && gestureMode === 'pan' && zoomScale > 1) {
          event.preventDefault();
          zoomPanX = panStartX + event.touches[0].clientX - touchStartX;
          zoomPanY = panStartY + event.touches[0].clientY - touchStartY;
          applyZoom();
        }
      }, { passive: false });
      zoomStage?.addEventListener('touchend', (event) => {
        if (event.touches.length === 1 && gestureMode === 'pinch') {
          const touch = event.touches[0];
          gestureMode = 'pan';
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
          panStartX = zoomPanX;
          panStartY = zoomPanY;
          return;
        }
        if (event.touches.length > 0) return;
        lightboxImage?.classList.remove('is-gesturing');
        const deltaX = (event.changedTouches[0]?.clientX || 0) - touchStartX;
        const deltaY = (event.changedTouches[0]?.clientY || 0) - touchStartY;
        if (gestureMode === 'swipe' && Math.abs(deltaX) >= 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
          const target = zoomIndex + (deltaX < 0 ? 1 : -1);
          if (target >= 0 && target < zoomSlides.length) showZoom(target);
        } else if (!pinchUsed && gestureMode !== 'pinch' && Math.abs(deltaX) < 12 && Math.abs(deltaY) < 12) {
          const now = Date.now();
          if (now - lastTapAt < 300) {
            zoomScale = zoomScale > 1 ? 1 : 2.5;
            zoomPanX = 0;
            zoomPanY = 0;
            applyZoom();
            lastTapAt = 0;
          } else {
            lastTapAt = now;
          }
        }
        gestureMode = '';
        pinchUsed = false;
      }, { passive: true });
      zoomStage?.addEventListener('touchcancel', () => {
        gestureMode = '';
        pinchUsed = false;
        lightboxImage?.classList.remove('is-gesturing');
        applyZoom();
      }, { passive: true });
      lightbox?.addEventListener('wheel', (event) => {
        if (!lightboxImage) return;
        event.preventDefault();
        zoomScale = Math.max(1, Math.min(3, zoomScale + (event.deltaY < 0 ? 0.2 : -0.2)));
        applyZoom();
      }, { passive: false });
      lightbox?.addEventListener('click', (event) => { if (event.target === lightbox) closeZoom(); });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && lightbox && !lightbox.hidden) closeZoom(); }, on);

      onVariant(({ data }) => {
        if (data.sectionId !== root.dataset.section || !data.variant?.featured_media?.id) return;
        const target = slides.findIndex((slide) => slide.dataset.mediaId === `${root.dataset.section}-${data.variant.featured_media.id}`);
        if (target >= 0) setActive(target);
      });
    }

    renderProductContent(root).then(() => initFeature(root));

    root.querySelector('[data-kla-reviews]')?.addEventListener('click', () => {
      const target = root.querySelector('[data-kla-reviews-target]') || document.querySelector('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-rev-widg, [data-judgeme-product-reviews]');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    const sticky = root.querySelector('[data-kla-sticky]');
    if (sticky) {
      const threshold = Number(sticky.dataset.threshold || 400);
      const stickyButton = sticky.querySelector('[data-kla-sticky-buy]');
      const stickyButtonLabel = stickyButton?.innerHTML; // metin etiketleri (<bold> vb.) korunur
      const submitButton = root.querySelector('.kla-buy button[type="submit"]');
      if (stickyButton && submitButton?.disabled) {
        stickyButton.disabled = true;
        if (window.variantStrings?.soldOut) stickyButton.textContent = window.variantStrings.soldOut;
      }
      let stickyVisible;
      const update = () => {
        const nextVisible = scrollY > threshold;
        if (nextVisible === stickyVisible) return;
        stickyVisible = nextVisible;
        sticky.classList.toggle('is-visible', nextVisible);
      };
      addEventListener('scroll', update, { passive: true, signal: ctl.signal });
      update();
      stickyButton?.addEventListener('click', () => submitButton?.click());
      onVariant(({ data }) => {
        if (data.sectionId !== root.dataset.section || !stickyButton) return;
        stickyButton.disabled = !data.variant?.available;
        if (data.variant?.available || !window.variantStrings?.soldOut) stickyButton.innerHTML = stickyButtonLabel;
        else stickyButton.textContent = window.variantStrings.soldOut;
      });
    }

    const modal = root.querySelector('[data-kla-installment-modal]');
    const list = modal?.querySelector('[data-kla-installment-list]');
    const priceInstallment = root.querySelector('[data-kla-price-installment]');
    const updateInstallments = (price) => {
      if (priceInstallment) {
        const coefficient = Number(priceInstallment.dataset.coefficient || 1.3022);
        const total = Math.round(price * coefficient);
        priceInstallment.querySelector('[data-kla-price-installment-amount]').textContent = money(Math.round(total / 12));
      }
      if (!list) return;
      list.dataset.price = price;
      list.querySelector('[data-kla-cash-total]').textContent = money(price);
      list.querySelectorAll('[data-kla-installment-row]').forEach((row) => {
        const count = Number(row.dataset.klaInstallmentRow);
        const coefficient = Number(row.dataset.klaInstallmentRate || 1);
        const total = Math.round(price * coefficient);
        row.querySelector('[data-kla-installment-amount]').textContent = money(Math.round(total / count));
        row.querySelector('[data-kla-installment-total]').textContent = money(total);
      });
    };
    if (list || priceInstallment) updateInstallments(Number(list?.dataset.price || priceInstallment?.dataset.price || 0));
    const preorderModal = root.querySelector('[data-kla-preorder-modal]');
    const modalDuration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 360;
    const openAnimatedModal = (target, trigger, closeSelector) => {
      if (!target) return;
      clearTimeout(target._klaCloseTimer);
      target._klaReturnFocus = trigger;
      target.hidden = false;
      target.classList.remove('is-closing');
      target.getBoundingClientRect();
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        target.classList.add('is-open');
        window.setTimeout(() => target.querySelector(closeSelector)?.focus(), modalDuration / 2);
      });
    };
    const closeAnimatedModal = (target) => {
      if (!target || target.hidden || target.classList.contains('is-closing')) return;
      target.classList.add('is-closing');
      target.classList.remove('is-open');
      clearTimeout(target._klaCloseTimer);
      target._klaCloseTimer = window.setTimeout(() => {
        target.hidden = true;
        target.classList.remove('is-closing');
        document.body.style.overflow = '';
        target._klaReturnFocus?.focus();
      }, modalDuration);
    };
    const installmentTrigger = root.querySelector('[data-kla-installments]');
    const preorderTrigger = root.querySelector('[data-kla-preorder-open]');
    installmentTrigger?.addEventListener('click', () => openAnimatedModal(modal, installmentTrigger, '[data-kla-installment-close]:not(.kla-modal__backdrop)'));
    modal?.querySelectorAll('[data-kla-installment-close]').forEach((el) => el.addEventListener('click', () => closeAnimatedModal(modal)));
    preorderTrigger?.addEventListener('click', () => openAnimatedModal(preorderModal, preorderTrigger, '[data-kla-preorder-close]:not(.kla-modal__backdrop)'));
    preorderModal?.querySelectorAll('[data-kla-preorder-close]').forEach((el) => el.addEventListener('click', () => closeAnimatedModal(preorderModal)));
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (modal && !modal.hidden) closeAnimatedModal(modal);
      if (preorderModal && !preorderModal.hidden) closeAnimatedModal(preorderModal);
    }, on);
    onVariant(({ data }) => {
      if (data.sectionId === root.dataset.section && data.variant?.price != null) updateInstallments(data.variant.price);
    });

    // Taksit penceresinin altındaki sepete ekle (kullanıcı isteği 28.10.2026): satıra basınca seçilir (✓), butona basınca
    // seçilen taksit sepete taşınır (sessionStorage kzInstPick, sepet sayfası kz-cart.js o satırı seçer), pencere kapanır ve
    // sayfanın ana Sepete Ekle butonu çalışır (aynı form: seçilen varyant, adet). Yazı ve kapalılık ana butondan
    const instBuy = modal?.querySelector('[data-kla-installment-buy]');
    const mainBuy = root.querySelector('.kla-buy button[type="submit"]');
    if (instBuy) {
      const sync = () => {
        const sp = mainBuy?.querySelector('span');
        if (sp && instBuy.firstElementChild) instBuy.firstElementChild.innerHTML = sp.innerHTML;
        instBuy.disabled = !mainBuy || mainBuy.disabled;
      };
      sync();
      if (mainBuy) new MutationObserver(sync).observe(mainBuy, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
      const pickRow = (row) => modal.querySelectorAll('[data-kla-pick]').forEach((r) => {
        const isOn = r === row;
        r.classList.toggle('is-picked', isOn);
        r.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        const mark = r.querySelector('span');
        if (mark) mark.textContent = isOn ? '✓' : '○';
      });
      // Her basışta sepete eklenin çevresindeki çerçeve 500 ms'de bir kez yanıp söner (kullanıcı isteği 28.10.2026; CSS
      // .kla-modal__buy.is-flash::after, yalnız opaklık; buton sabit)
      const buyBox = instBuy.closest('.kla-modal__buy');
      const flash = () => {
        if (!buyBox) return;
        buyBox.classList.remove('is-flash');
        void buyBox.offsetWidth;
        buyBox.classList.add('is-flash');
      };
      buyBox?.addEventListener('animationend', () => buyBox.classList.remove('is-flash'));
      modal.addEventListener('click', (event) => { const row = event.target.closest('[data-kla-pick]'); if (row) { pickRow(row); flash(); } });
      modal.addEventListener('keydown', (event) => {
        const row = event.target.closest?.('[data-kla-pick]');
        if (row && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); pickRow(row); flash(); }
      });
      instBuy.addEventListener('click', () => {
        if (!mainBuy || mainBuy.disabled) return;
        const picked = modal.querySelector('[data-kla-pick].is-picked');
        try {
          if (picked) sessionStorage.setItem('kzInstPick', JSON.stringify({ n: Number(picked.dataset.klaPick) || 1, at: Date.now() }));
          else sessionStorage.removeItem('kzInstPick');
        } catch (error) { /* gizli sekme: seçim taşınmaz */ }
        closeAnimatedModal(modal);
        mainBuy.click();
      });
    }
  };

  const start = () => document.querySelectorAll('product-info.kla-product').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
  // Editörde bölüm silinince dinleyicileri ve gözlemcileri kalkar
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll?.('product-info.kla-product').forEach((root) => {
    const key = root.dataset.section || root.id; teardown.get(key)?.forEach((off) => off()); teardown.delete(key);
  }));
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('product-info.kla-product').forEach((root) => { delete root.dataset.klaReady; init(root); }));
})();
